import React from 'react';
import { useState, useEffect, useRef } from 'react';
import {convertNumberToWords, formatNumberWithCommas, getAbbreviationForPi} from '../utilities';
import axios from '../axios';
import { useParams } from 'react-router-dom';
import { SalesContractToolbar, emptySalesContract, scDate, toScNo } from './SalesContractOptions';

const GroupPiPDF = () => {
  const {groupPiId } = useParams();
  const [groupPiInfo, setGroupPiInfo] = useState([]);
  const [groupPiNo, setGroupPiNo] = useState('');
  const [groupPiDate, setGroupPiDate] = useState(0);
  const [companyTitle, setCompanyTitle] = useState(localStorage.getItem('company'));
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fontScale, setFontScale] = useState(100); // percent, 70–120
  const [rowSpacing, setRowSpacing] = useState(100); // percent, 40–100
  const [pendingSave, setPendingSave] = useState(false);
  // Short PI: one-line version of the group PI for bond / LC opening.
  const [summary, setSummary] = useState(null);       // saved short PI, null if none
  const [showSummary, setShowSummary] = useState(false);
  const [salesContract, setSalesContract] = useState(emptySalesContract);
  const [summaryForm, setSummaryForm] = useState(null); // non-null while the form is open
  const [summarySaving, setSummarySaving] = useState(false);
  const printableRef = useRef(null);

  // Generic contenteditable sweep — same technique as PiPDF/SavedCommercialViewer.
  // Always flips elements that already carry contenteditable too, so re-editing
  // a page that was edited before keeps working.
  useEffect(() => {
    if (!printableRef.current) return;
    const nodes = printableRef.current.querySelectorAll('td, th, span, strong, h1, h2, div');
    nodes.forEach((el) => {
      if (
        el.hasAttribute('contenteditable') ||
        el.children.length === 0 ||
        el.tagName === 'TD' ||
        el.tagName === 'TH'
      ) {
        el.setAttribute('contenteditable', editMode ? 'true' : 'false');
      }
    });
  }, [editMode, loading]);

  const handleSaveClick = () => {
    if (editMode) {
      setEditMode(false);
      setPendingSave(true);
    } else {
      captureAndSave();
    }
  };

  const captureAndSave = async () => {
    if (!printableRef.current) return;
    setSaving(true);
    const htmlContent = printableRef.current.outerHTML;
    try {
      await axios.post(`${window.backendUrl}/save_group_pi_document/${groupPiId}`, { htmlContent });
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (pendingSave && !editMode) {
      captureAndSave();
      setPendingSave(false);
    }
  }, [editMode, pendingSave]);

  const decreaseFont = () => setFontScale((prev) => Math.max(70, prev - 5));
  const increaseFont = () => setFontScale((prev) => Math.min(120, prev + 5));
  const decreaseSpacing = () => setRowSpacing((prev) => Math.max(40, prev - 10));
  const increaseSpacing = () => setRowSpacing((prev) => Math.min(100, prev + 10));

  useEffect(() => {
    const storedCompany = localStorage.getItem('company');
    if (storedCompany) {
      setCompanyTitle(storedCompany);
    }
  }, []);

  useEffect(() => {
    if (groupPiId) {
      fetch_group_pi_pdf_info();
      fetch_group_pi_summary();
    }
  }, [groupPiId]);

  const fetch_group_pi_summary = () => {
    axios.get(`${window.backendUrl}/get_group_pi_summary/${groupPiId}`)
      .then((response) => {
        if (response.data.found) {
          const row = response.data.summary;
          let lines = null;
          try { lines = row.LinesJson ? JSON.parse(row.LinesJson) : null; } catch (e) { lines = null; }
          setSummary(lines && lines.length ? { lines } : { legacy: row });
          setShowSummary(true);
        }
      })
      .catch((error) => console.log(error));
  };

  // Pre-fill the short PI form from the saved short PI, or from the real orders: one line per
  // item name + spec, with its pieces, its real value as the rate (value ÷ qty), the measurement
  // with the most pieces and the PO/style of every order that has that item.
  const openSummaryForm = () => {
    if (summary) {
      setSummaryForm({ lines: getSummaryLines(summary).map((line) => ({ ...line })) });
      return;
    }
    const groups = [];
    groupPiInfo.forEach((order) => {
      const poLine = [order.lpd_po_no, order.style].filter(Boolean).join(', ');
      order.measurements.forEach((m) => {
        let g = groups.find((x) => x.itemName === (m.itemName || '') && x.specification === (m.specification || ''));
        if (!g) {
          g = { itemName: m.itemName || '', specification: m.specification || '', biggest: null, poLines: [], quantity: 0, value: 0 };
          groups.push(g);
        }
        g.quantity += m.orderQuantity;
        g.value += m.unitPrice * m.orderQuantity;
        if (!g.biggest || m.orderQuantity > g.biggest.orderQuantity) g.biggest = m;
        if (poLine && !g.poLines.includes(poLine)) g.poLines.push(poLine);
      });
    });
    setSummaryForm({
      lines: groups.map((g) => ({
        itemName: g.itemName,
        specification: g.specification,
        measurement: ((g.biggest && g.biggest.measurement) || '').split('\n')[0],
        poList: g.poLines.join('\n'),
        quantity: g.quantity,
        unitPrice: g.quantity > 0 ? g.value / g.quantity : 0,
      })),
    });
  };

  const blankLine = () => ({ itemName: '', specification: '', measurement: '', poList: '', quantity: '', unitPrice: 0, priceText: '' });

  const updateLine = (index, changes) => {
    setSummaryForm((prev) => ({ lines: prev.lines.map((line, i) => (i === index ? { ...line, ...changes } : line)) }));
  };

  const saveSummary = async () => {
    setSummarySaving(true);
    try {
      const lines = summaryForm.lines.map((line) => ({
        itemName: line.itemName,
        specification: line.specification,
        measurement: line.measurement,
        poList: line.poList,
        quantity: Number(line.quantity) || 0,
        unitPrice: Number(line.unitPrice) || 0,
      }));
      await axios.post(`${window.backendUrl}/save_group_pi_summary/${groupPiId}`, { lines });
      setSummary({ lines });
      setShowSummary(true);
      setSummaryForm(null);
    } catch (err) {
      console.log(err);
      alert('Could not save the short PI. Please try again.');
    } finally {
      setSummarySaving(false);
    }
  };

  const removeSummary = async () => {
    if (!window.confirm('Remove the short PI and go back to the detailed group PI?')) return;
    try {
      await axios.post(`${window.backendUrl}/remove_group_pi_summary/${groupPiId}`);
      setSummary(null);
      setShowSummary(false);
      setSummaryForm(null);
    } catch (err) {
      console.log(err);
      alert('Could not remove the short PI. Please try again.');
    }
  };

  const fetch_group_pi_pdf_info = () => {
    axios.get(`${window.backendUrl}/get_group_pi_pdf_info/${groupPiId}`)
      .then((response) => {
        setGroupPiInfo(response.data.data);
        setGroupPiNo(response.data.groupPiNo);
        setGroupPiDate(response.data.groupPiDate);
        setLoading(false);
      })
      .catch((error) => {
        // console.log(error);
        setLoading(false);
      });
    // console.log(groupPiInfo)
    // setLoading(false);
  };

//   useEffect(() => {
//     if (groupPiInfo) {
//       console.log(groupPiInfo);
//     }
//   }, [groupPiInfo]);

  if (loading) {
    return <></>
  }

  // Short PI always keeps the real grand total; only the rate is derived from it.
  const grandTotal = Object.values(groupPiInfo).reduce((acc, order) =>
    acc + order.measurements.reduce((priceAcc, measurement) => priceAcc + (measurement.unitPrice * measurement.orderQuantity), 0), 0);
  const piRefs = groupPiInfo
    .filter((order) => order.jobNo)
    .map((order) => `${salesContract.on ? 'SC' : 'PI'}/${getAbbreviationForPi(order.party_name || '')}/${order.jobNo}`)
    .join(', ');
  // Short PI lines. A short PI saved before lines existed is one line priced at total ÷ qty.
  const getSummaryLines = (saved) => {
    if (!saved) return [];
    if (saved.lines) return saved.lines;
    const qty = Number(saved.legacy.Quantity) || 0;
    return [{
      itemName: saved.legacy.ItemName,
      specification: saved.legacy.Specification,
      measurement: saved.legacy.Measurement,
      poList: saved.legacy.PoList,
      quantity: qty,
      unitPrice: qty > 0 ? grandTotal / qty : 0,
    }];
  };
  const lineTotal = (line) => Math.round((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) * 100) / 100;
  const linesTotal = (lines) => Math.round(lines.reduce((acc, line) => acc + lineTotal(line), 0) * 100) / 100;
  const linesQty = (lines) => lines.reduce((acc, line) => acc + (Number(line.quantity) || 0), 0);
  // Up to 6 decimals, trailing zeros dropped (at least 2).
  const formatRate = (n) => {
    const fixed = (Number(n) || 0).toFixed(6).replace(/0+$/, '');
    const [whole, dec = ''] = fixed.split('.');
    return `${whole}.${dec.padEnd(2, '0')}`;
  };
  const realTotal = Math.round(grandTotal * 100) / 100;
  const summaryLines = showSummary && summary ? getSummaryLines(summary) : [];
  const shownTotal = showSummary && summary ? linesTotal(summaryLines) : grandTotal;
  const formLines = summaryForm ? summaryForm.lines : [];
  const formTotal = linesTotal(formLines);
  const formValid = formLines.length > 0 && formLines.every((line) => Number(line.quantity) > 0 && Number(line.unitPrice) >= 0);
  // Scale every rate so the short PI adds up to the real group PI total again.
  const fitToRealTotal = () => {
    if (!(formTotal > 0)) return;
    const factor = realTotal / formTotal;
    setSummaryForm((prev) => ({ lines: prev.lines.map((line) => ({ ...line, unitPrice: (Number(line.unitPrice) || 0) * factor, priceText: undefined })) }));
  };

  // Row spacing (density) — same mechanism as PiPDF/CommercialPDF. At 100% no
  // CSS is emitted, so default output is unchanged.
  const spacingFactor = rowSpacing / 100;
  const spacingLineHeight = (1.15 + ((rowSpacing - 40) / 60) * 0.35).toFixed(2);
  const rowSpacingCss =
    rowSpacing < 100
      ? `
        /* Header cells carry explicit 8px vertical padding; body cells carry
           none (table has cellPadding="0"), so scaling their (zero) base is a
           correct no-op there and the visible compacting comes from line-height. */
        .group-pi-item-table th {
          padding-top: calc(8px * ${spacingFactor}) !important;
          padding-bottom: calc(8px * ${spacingFactor}) !important;
        }
        .group-pi-item-table th, .group-pi-item-table td {
          line-height: ${spacingLineHeight} !important;
        }
      `
      : '';

  return (
    <>
      <div className="no-print" style={{ margin: '8px 8px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode((prev) => !prev)}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        <button onClick={handleSaveClick} disabled={saving}>
          {saving ? 'Saving...' : 'Save Document'}
        </button>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" onClick={decreaseFont} disabled={fontScale <= 70} title="Decrease font size">A−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{fontScale}%</span>
          <button type="button" onClick={increaseFont} disabled={fontScale >= 120} title="Increase font size">A+</button>
        </span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>Row spacing</span>
          <button type="button" onClick={decreaseSpacing} disabled={rowSpacing <= 40} title="Make table rows more compact">−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{rowSpacing}%</span>
          <button type="button" onClick={increaseSpacing} disabled={rowSpacing >= 100} title="Make table rows taller">+</button>
        </span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" onClick={openSummaryForm} title="One-line version of this group PI for bond / LC opening">
            {summary ? 'Edit Short PI' : 'Make Short PI'}
          </button>
          {summary && (
            <button type="button" onClick={() => setShowSummary((prev) => !prev)}>
              {showSummary ? 'Show Detailed PI' : 'Show Short PI'}
            </button>
          )}
        </span>
        <SalesContractToolbar salesContract={salesContract} setSalesContract={setSalesContract} />
      </div>
      {summaryForm && (
        <div className="no-print" style={{ margin: '8px', padding: '12px', border: '1px solid #999', borderRadius: '6px', background: '#f7f7f7', maxWidth: '1100px', fontSize: '13px' }}>
          <strong>Short PI</strong> — put the jobs into as few lines as you need. Every box can be changed; each line's total is qty × unit price.
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '4px' }}>#</th>
                <th style={{ padding: '4px' }}>Style / PO No. <small>(one per line)</small></th>
                <th style={{ padding: '4px' }}>Item Name</th>
                <th style={{ padding: '4px' }}>Spec.</th>
                <th style={{ padding: '4px' }}>Measurement</th>
                <th style={{ padding: '4px' }}>Qty (Pcs)</th>
                <th style={{ padding: '4px' }}>Unit Price ($)</th>
                <th style={{ padding: '4px', textAlign: 'right' }}>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formLines.map((line, i) => (
                <tr key={i} style={{ verticalAlign: 'top' }}>
                  <td style={{ padding: '4px' }}>{i + 1}</td>
                  <td style={{ padding: '4px' }}><textarea rows={4} style={{ width: '100%', minWidth: '200px' }} value={line.poList} onChange={(e) => updateLine(i, { poList: e.target.value })} /></td>
                  <td style={{ padding: '4px' }}><input style={{ width: '100%', minWidth: '160px' }} value={line.itemName} onChange={(e) => updateLine(i, { itemName: e.target.value })} /></td>
                  <td style={{ padding: '4px' }}><input style={{ width: '70px' }} value={line.specification} onChange={(e) => updateLine(i, { specification: e.target.value })} /></td>
                  <td style={{ padding: '4px' }}><input style={{ width: '120px' }} value={line.measurement} onChange={(e) => updateLine(i, { measurement: e.target.value })} /></td>
                  <td style={{ padding: '4px' }}><input type="number" min="1" style={{ width: '80px' }} value={line.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} /></td>
                  <td style={{ padding: '4px' }}>
                    <input type="number" min="0" step="any" style={{ width: '100px' }}
                      value={line.priceText !== undefined ? line.priceText : formatRate(line.unitPrice)}
                      onChange={(e) => updateLine(i, { priceText: e.target.value, unitPrice: Number(e.target.value) || 0 })} />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'right', whiteSpace: 'nowrap' }}>${formatNumberWithCommas(lineTotal(line).toFixed(2))}</td>
                  <td style={{ padding: '4px' }}>
                    {formLines.length > 1 && (
                      <button type="button" title="Remove this line" style={{ color: '#b00' }}
                        onClick={() => setSummaryForm((prev) => ({ lines: prev.lines.filter((_, j) => j !== i) }))}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan="5" style={{ padding: '4px' }}>
                  <button type="button" onClick={() => setSummaryForm((prev) => ({ lines: [...prev.lines, blankLine()] }))}>+ Add Item</button>
                </td>
                <td style={{ padding: '4px' }}><strong>{linesQty(formLines)}</strong></td>
                <td style={{ padding: '4px', textAlign: 'right' }}><strong>Grand Total:</strong></td>
                <td style={{ padding: '4px', textAlign: 'right', whiteSpace: 'nowrap' }}><strong>${formatNumberWithCommas(formTotal.toFixed(2))}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          </div>
          {Math.abs(formTotal - realTotal) >= 0.01 ? (
            <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fff3cd', border: '1px solid #e0b84c', borderRadius: '4px', color: '#664d03' }}>
              ⚠ The Short PI total <strong>${formatNumberWithCommas(formTotal.toFixed(2))}</strong> does not match the real Group PI total <strong>${formatNumberWithCommas(realTotal.toFixed(2))}</strong>
              {' '}({formTotal > realTotal ? '+' : '−'}${formatNumberWithCommas(Math.abs(formTotal - realTotal).toFixed(2))}).
              {formTotal > 0 && (
                <button type="button" onClick={fitToRealTotal} style={{ marginLeft: '8px' }}>Adjust prices to match real total</button>
              )}
            </div>
          ) : (
            <div style={{ marginTop: '8px', color: '#2e7d32' }}>✓ Matches the real Group PI total ${formatNumberWithCommas(realTotal.toFixed(2))}.</div>
          )}
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
            <button type="button" onClick={saveSummary} disabled={summarySaving || !formValid}>
              {summarySaving ? 'Saving...' : 'Save Short PI'}
            </button>
            <button type="button" onClick={() => setSummaryForm(null)}>Cancel</button>
            {!formValid && <span style={{ color: '#b00', alignSelf: 'center' }}>Every line needs a qty above 0 and a price.</span>}
            {summary && (
              <button type="button" onClick={removeSummary} style={{ marginLeft: 'auto', color: '#b00' }}>Remove Short PI</button>
            )}
          </div>
        </div>
      )}
      {!summaryForm && showSummary && summary && Math.abs(shownTotal - realTotal) >= 0.01 && (
        <div className="no-print" style={{ margin: '8px', padding: '8px 10px', background: '#fff3cd', border: '1px solid #e0b84c', borderRadius: '4px', color: '#664d03', fontSize: '13px', maxWidth: '1100px' }}>
          ⚠ This Short PI shows <strong>${formatNumberWithCommas(shownTotal.toFixed(2))}</strong>, but the real Group PI total is <strong>${formatNumberWithCommas(realTotal.toFixed(2))}</strong>. (This note does not print.)
        </div>
      )}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          [contenteditable] { outline: none !important; background-color: transparent !important; }
        }
      `}</style>
    <body ref={printableRef} style={{ zoom: fontScale / 100 }}>
    {rowSpacingCss && <style>{rowSpacingCss}</style>}
    <table width="100%" border="0" cellSpacing="5" cellPadding="0" style={{ width: '100%', fontSize:'12px', borderCollapse: 'collapse', marginTop:'15px' }}>
      <tbody>
        <tr>
          <td align="center" valign="top" width="130">
            <img
              style={{ maxHeight: '70px' }}
              src="/Images/miahandsonslogo.png"
              alt="MIAH & SONS INDUSTRIES LTD."
            />
          </td>
          <td align="left" valign="top" nowrap="nowrap">
            <p>
              <strong style={{ fontSize: '22px' }}>{companyTitle}</strong>
              <br />
              <strong style={{ fontSize: '15px', color: '#61190d' }}>Factory / Office: </strong>
              {(companyTitle === 'MIAH & SONS INDUSTRIES LTD.' || companyTitle === 'Tanvir Printing & Packaging IND.')
                ? 'Holding No; 110/1, Mouza: Barabo, Po.Shakaswar, P.S Kaliakoir, Gazipur-1703, Bangladesh'
                : companyTitle === 'Propack Trims & Accessories Industries'
                  ? 'Muradpur, Shakaswar, Kaliakoir, Gazipur-1703, Bangladesh.'
                  : '1234 Main St, Springfield, IL 62704, USA'}
              <br />
              BIN/VAT Reg. : 000686131-1205
            </p>
          </td>
          <td width="40%" align="left" valign="middle">
            <h2 align="center" style={{ width: '270px',border: '1px solid #333',padding: '8px 20px', marginTop: '0', lineHeight:'18px', fontSize:'20px'}}>
              <strong>{salesContract.on ? 'SALES CONTRACT' : 'PROFORMA INVOICE'}</strong>
            </h2>
            
          </td>    
        </tr>
        
        <tr>
          <td colSpan="3" align="center">
          <hr className='challan_hr' style={{ border: '1px solid black', width: '100%' }} />
          </td>
        </tr>
        <tr valign="top" >
          <td colSpan="2" className='group_pi_to_and_address'>
            <strong>To: </strong> {groupPiInfo[0].party_name}
            <br />
            <div style={{width:'400px',wordWrap: 'break-word', whiteSpace: 'normal' }}>
              <strong>Address: </strong>{groupPiInfo[0].address}
            </div>
            <div style={{width:'400px',wordWrap: 'break-word', whiteSpace: 'normal' }}>
              <strong>Delivery Address: </strong> {groupPiInfo[0].delivery_station}
            </div>
            <strong>Account/Buyer:</strong> {groupPiInfo[0].buyer_name}
            <br />
            <strong>ATTN:</strong> {groupPiInfo[0].attention}
            <br />
          </td>
          <td align="left" >
            <strong>A. General Information</strong> 
            <br />
            <strong>{salesContract.on ? 'Sales Contract No.:' : 'Proforma Invoice No.:'}</strong> {salesContract.on ? toScNo(groupPiNo) : groupPiNo}
            <br />
            <strong>{salesContract.on ? 'Sales Contract Date:' : 'Proforma Invoice Date:'}</strong> {new Date(groupPiDate * 1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dhaka' }).replace(/\//g, '.')}
            <br />
            <strong>IRC No.:</strong> 260326120555921
            <br />
            <strong>ERC No.:</strong> 260326210959520
            <br />
            <strong>BIN No.:</strong> 000686131-1205
            <br />
            <strong>TIN No.:</strong> 117556497007
          </td>
        </tr>
        <tr>
          <td colSpan="2" align="left" style={{ paddingBottom: '15px' }}>
            
          </td>
          <td align="right" style={{ paddingBottom: '15px' }}>
            <div
              align="center"
              style={{
                width: '180px',
                border: '1px solid #333',
                padding: '8px 15px',
                marginTop: '0',
              }}
            >
              <strong>H.S. Code: 4819.10.00</strong>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    {showSummary && summary ? (
    <table className="group-pi-item-table" width="100%" border="1" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
      <thead style={{fontSize:'12px'}}>
        <tr>
            {['SL', 'Style/Order /PO No.', 'Item Name', 'Spec.', 'Measurement', 'Qty', 'Unit Price', 'Total Amount'].map((heading) => (
              <th key={heading} style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>{heading}</th>
            ))}
        </tr>
      </thead>
      <tbody className='challan_pdf_table' style={{ fontSize: '12px' }}>
        {summaryLines.map((line, i) => (
          <tr key={i}>
            <td style={{ textAlign: 'center' }}>{i + 1}</td>
            <td style={{ textAlign: 'left', padding: '4px 8px', whiteSpace: 'pre-line' }}>{line.poList}</td>
            <td style={{ textAlign: 'center' }}>{line.itemName}</td>
            <td style={{ textAlign: 'center' }}>{line.specification}</td>
            <td style={{ textAlign: 'center' }}>{line.measurement}</td>
            <td style={{ textAlign: 'center' }}>{line.quantity}/Pcs</td>
            <td style={{ textAlign: 'center' }}>${formatRate(line.unitPrice)}</td>
            <td style={{ textAlign: 'center' }}>${formatNumberWithCommas(lineTotal(line).toFixed(2))}</td>
          </tr>
        ))}
        <tr>
          <td colSpan="4" style={{ textAlign: 'left', verticalAlign: 'middle', padding: '6px 10px', lineHeight: '1.5' }}>
            <strong>{salesContract.on ? 'SC No.: ' : 'PI No.: '}</strong>{piRefs}
          </td>
          <td style={{ textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap' }}><strong>Grand Total:</strong></td>
          <td style={{ textAlign: 'center' }}><strong>{linesQty(summaryLines)}</strong></td>
          <td></td>
          <td style={{ textAlign: 'center' }}><strong>${formatNumberWithCommas(shownTotal.toFixed(2))}</strong></td>
        </tr>
      </tbody>
    </table>
    ) : (
    <table className="group-pi-item-table" width="100%" border="1" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
      <thead style={{fontSize:'12px'}}>
        <tr>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>SL</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Date</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>LPD PO No.</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Style/Order /File No.</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Buyer Name</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Item Name</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Spec.</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Measurement</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Qty</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Total Sq.Mt.</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Unit Price</th>
            <th style={{padding:'8px 10px',backgroundColor: 'rgba(0, 0, 0, 0.05)', border: '1px solid #000', textAlign: 'center'}}>Total Amount</th>
        </tr>
    </thead>

      <tbody className='challan_pdf_table' style={{ fontSize: '12px' }}>
        {groupPiInfo.map((order,index) => {
            const { created_date, lpd_po_no, style, buyer_name, measurements } = order;
            
            // Calculate the number of measurements for rowspan
            const measurementCount = measurements.length + 1;

            return (
            <React.Fragment>
                <tr>
                <td rowSpan={measurementCount} style={{ textAlign: 'center' }}>{index+1}</td>
                <td rowSpan={measurementCount} style={{ textAlign: 'center' }}>{new Date(created_date * 1000).toLocaleDateString()}</td>
                <td rowSpan={measurementCount} style={{ textAlign: 'center' }}>{lpd_po_no}</td>
                <td rowSpan={measurementCount} style={{ textAlign: 'center' }}>{style}</td>
                <td rowSpan={measurementCount} style={{ textAlign: 'center' }}>{buyer_name}</td>
                <td style={{textAlign:'center'}}>{measurements[0].itemName}</td>
                <td style={{textAlign:'center'}}>{measurements[0].specification}</td>
                <td style={{textAlign:'center'}}>
                        {measurements[0].height === 0
                            ? `${measurements[0].length}x${measurements[0].width} ${measurements[0].unit}`
                            : `${measurements[0].length}x${measurements[0].width}x${measurements[0].height} ${measurements[0].unit}`}
                    </td>
                <td style={{textAlign:'center'}}>{measurements[0].orderQuantity}/Pcs</td>
                <td style={{textAlign:'center'}}>{measurements[0].totalSquareMeter}</td>
                <td style={{textAlign:'center'}}>${(measurements[0].unitPrice).toFixed(5)}</td>
                <td style={{textAlign:'center'}}>${(measurements[0].unitPrice * measurements[0].orderQuantity).toFixed(2)}</td>
                </tr>
                {measurements.slice(1).map((measurement, index) => (
                <tr key={index}>
                    <td style={{textAlign:'center'}}>{measurement.itemName}</td>
                    <td style={{textAlign:'center'}}>{measurement.specification}</td>
                    {/* <td style={{textAlign:'center'}}>
                        {measurement.height === 0
                            ? `${measurement.length}x${measurement.width} ${measurement.unit}`
                            : `${measurement.length}x${measurement.width}x${measurement.height} ${measurement.unit}`}
                    </td> */}
                    <td style={{textAlign:'center'}}>
                            {measurement.measurement}
                    </td>
                    <td style={{textAlign:'center'}}>{measurement.orderQuantity}/Pcs</td>
                    <td style={{textAlign:'center'}}>{measurement.totalSquareMeter}</td>
                    <td style={{textAlign:'center'}}>${(measurement.unitPrice).toFixed(5)}</td>
                    <td style={{textAlign:'center'}}>${(measurement.unitPrice * measurement.orderQuantity).toFixed(2)}</td>
                </tr>
                ))}
                <tr>
                    <td colSpan="3" style={{ textAlign: 'right' }}><strong>LPD PO No. Total: </strong></td>
                    <td style={{textAlign:'center'}}><strong>{measurements.reduce((acc, measurement) => acc + measurement.orderQuantity, 0)}</strong></td>
                    <td colSpan="2"></td>
                    <td style={{textAlign:'center'}}><strong>${formatNumberWithCommas((measurements.reduce((acc, measurement) => acc + (measurement.unitPrice * measurement.orderQuantity), 0)).toFixed(2))}</strong></td>
                </tr>
            </React.Fragment>
            );
        })}
        <tr>
            <td colSpan="6" style={{ textAlign: 'left', verticalAlign: 'middle', padding: '6px 10px', lineHeight: '1.5' }}>
                <strong>{salesContract.on ? 'SC No.: ' : 'PI No.: '}</strong>
                {piRefs}
            </td>
            <td colSpan="2" style={{ textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap' }}><strong>Grand Total:</strong></td>
            <td style={{textAlign:'center'}}>
                <strong>
                    {Object.values(groupPiInfo).reduce((acc, order) => 
                        acc + order.measurements.reduce((qtyAcc, measurement) => qtyAcc + measurement.orderQuantity, 0), 0)}
                </strong>
            </td>
            <td style={{textAlign:'center'}}>
                <strong>
                    {Object.values(groupPiInfo).reduce((acc, order) =>
                        acc + order.measurements.reduce((sqmAcc, measurement) => sqmAcc + (Number(measurement.totalSquareMeter) || 0), 0), 0).toFixed(3)}
                </strong>
            </td>
            <td></td>
            <td style={{textAlign:'center'}}>
                <strong>
                    ${formatNumberWithCommas(
                        Object.values(groupPiInfo).reduce((acc, order) => 
                            acc + order.measurements.reduce((priceAcc, measurement) => priceAcc + (measurement.unitPrice * measurement.orderQuantity), 0), 0).toFixed(2)
                        )}
                </strong>
            </td>
        </tr>
        {/* <tr>
            <td colSpan="12" style={{ textAlign: 'left', padding: '10px', backgroundColor: '#f9f9f9' }}>
                <strong>Special Notes:</strong> Your content for the first full-width row goes here.
            </td>
        </tr>
        <tr>
            <td colSpan="12" style={{ textAlign: 'left', padding: '10px' }}>
                <strong>Additional Information:</strong> Your content for the second full-width row goes here.
            </td>
        </tr> */}
    </tbody>


    </table>
    )}
{/* /////////////////////////////////////////////////////////////////////////////////////////////// */}
    <div style={{ width: '95%', height: '50px', margin: '15px auto 0', padding: '0', fontSize:'12px'}}>
      <table width="100%" border="1" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td align="left" className='in_words_pi'>
              <strong>
                In Words (USD): <strong>{convertNumberToWords(shownTotal.toFixed(2))}</strong>
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <table border="0" cellSpacing="5" cellPadding="3" style={{ marginLeft: '50px', fontSize:'12px' }}>
      <tbody className='group_pi_terms_and_conditions'>
        <tr >
          <td align="left" colSpan="3">
            <br />
            <strong >C. Terms & Condition:</strong>
          </td>
        </tr>
        <br />
        <div style={{marginLeft:'23px'}}>
        {/* <tr>
          <td align="left" nowrap="nowrap" width="5%">
          1. Payment: Irrevocable Letter of Credit at {groupPiInfo[0].credit_days} days from the date of Delivery Challan to be opened in favor of <strong>{companyTitle}, Nandan Icon, House # 45, Lavel-5, Road #15, Rabindra Saroni, Sector #3, Uttara, Dhaka-1230, Bangladesh.</strong>
          </td>
        </tr> */}
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            1. Payment:{" "}
            {groupPiInfo[0].credit_days === 0
              ? "Irrevocable Letter of Credit at sight to be opened in favor of "
              : `Irrevocable Letter of Credit at ${groupPiInfo[0].credit_days} days from the date of Delivery Challan to be opened in favor of `}
            <strong>
              {companyTitle}, Nandan Icon, House # 45, Lavel-5, Road #15, Rabindra Saroni, Sector #3, Uttara, Dhaka-1230, Bangladesh.
            </strong>
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">2. L/C Advising Bank : 
          <strong>{groupPiInfo[0].bank_name}, </strong>A/C # <strong>{groupPiInfo[0].bank_account_no}</strong>, {groupPiInfo[0].bank_address}, SWIFT Code : <strong>{groupPiInfo[0].bank_swift_code}</strong>
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">3.Payment to be made in <strong>U.S Dollar</strong> </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            4. Partial Shipment: Allowed.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            5. Freight Charge: Free of Cost
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            6. Incoterms CPT: Carriage Paid To (2000 & 2010)
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            7. Delivery Terms / Inco Term : CPT. (Openers Factory)
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            8. Maturity date should be calculated from the date of Delivery Challan / Negotiation.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            9. All Banking Charges inside opener's bank counter on account of opener and outside opener's bank counter on account of beneficiary.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            10. Clause of Bank Officer's signature on Delivery Challan and or acceptance after physical verification of goods by the bank officer's is/are not allowed in L/C.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            11. Payment after Export Realization Clause not allowed in the L/C.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            12. L/C must incorporate delivery validity 30 days from the date of L/C.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            13. Document Presentation Period : 15 days from the date of delivery challan.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            14. L/C should be freely Negotiable
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            15. Tolerance: 10% (+/-) both in quantity and value are acceptable.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            16. Interest to be paid at LIBOR for usance period.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            17. Overdue Interest @ 13.50% p.a shall have to be paid by the opener for late payment.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            18. Terms & Conditions of this Proforma Invoice deemed to be an integral part of L/C.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            19. Utilization Declaration (UD), along with the bank attested copy of L/C or sales contract has to be in place within 3 working days to back L/C or sales contract.
          </td>
        </tr>
        <tr>
          <td align="left" nowrap="nowrap" width="5%">
            {salesContract.on
              ? `20. Shipment Date: ${scDate(salesContract.shipmentDate)}, Expiry Date: ${scDate(salesContract.expiryDate)}`
              : '20. PI Validity: 35 days'}
          </td>
        </tr>
        </div>
        {/* {piInfo.note ? (<tr>
          <td colspan="3" align="left">
            <br/>
            <div style={{ textAlign: 'left', width: '95%', border: '1px solid #333', padding: '8px 15px', marginTop: 0 }}>
              Remarks. {piInfo.note}	
            </div>
          </td>
        </tr>) : null} */}
      </tbody>
    </table>


    <table style={{marginTop:'28px'}} align="right" width="100%" border="0" cellSpacing="0" cellPadding="0">
      <tbody>
        <tr>
          <td style={{fontSize:'12px'}} width="50%" align="center">
            <strong>{groupPiInfo[0].party_name}</strong><br/>
            <div style={{width:'300px', borderTop:'1px solid black', marginTop:'85px'}}>Accepted By</div>
          </td>
          <td>
            <td width="50%" align="center">
              <br />
              <div style={{ width: '300px', paddingBottom: '15px' }}>&nbsp;</div>
            </td>
            <td width="50%" align="center">
              <strong style={{ fontSize: '15px' }}>{companyTitle}</strong>
              <br />
              <img style={{ maxWidth: '150px' }} src="/Images/miah_and_sons_signature.png" alt="AUTHORISED SIGNATURE" />
              <br />
              <span style={{ borderTop: '1px solid black', fontSize:'12px' }}>AUTHORISED SIGNATURE</span>
            </td>
            </td>
        </tr>
      </tbody>
    </table>
    </body>
    </>
  );
};

export default GroupPiPDF;